import { useForm } from "@inertiajs/react";
import { Check, ChevronsUpDown, Edit3, Lock, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TRUNCATE_THRESHOLD } from "@/lib/constants";
import { cn } from "@/lib/utils";

type EditableValue = string | number | null | undefined;

interface Option {
  label: string;
  value: string | number;
}

interface InlineEditableComboboxProps {
  label: string;
  value: string | number | null | undefined;
  field: string;
  updateUrl: string;
  icon?: React.ElementType;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  formatValue?: (value: string | number | null | undefined) => string;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

const defaultFormatValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return "No asignado";
  return String(value);
};

export default function InlineEditableCombobox({
  label,
  value,
  field,
  updateUrl,
  icon: Icon,
  options = [],
  placeholder = "Seleccionar...",
  disabled = false,
  formatValue = defaultFormatValue,
  searchPlaceholder = "Buscar...",
  emptyMessage = "No se encontraron resultados",
}: InlineEditableComboboxProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [open, setOpen] = useState(false);

  const { data, setData, patch, processing, errors, reset } = useForm<
    Record<string, EditableValue>
  >({
    [field]: value,
  });

  const startEditing = () => {
    if (!disabled) {
      reset();
      setData(field, value);
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setOpen(false);
    reset();
    setData(field, value);
  };

  const saveChanges = () => {
    patch(updateUrl, {
      preserveScroll: true,
      onSuccess: () => {
        setIsEditing(false);
        setOpen(false);
      },
      onError: () => {
        // Keep editing mode open if there are errors
      },
    });
  };

  const getSelectedOption = () => {
    return options.find(
      (option) => String(option.value) === String(data[field]),
    );
  };

  const handleSelect = (optionValue: string) => {
    setData(field, optionValue);
    setOpen(false);
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          {Icon && <Icon className="h-4 w-4" />}
          {label}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between h-9"
                >
                  <span
                    className="truncate min-w-0"
                    title={getSelectedOption()?.label ?? placeholder}
                  >
                    {getSelectedOption()?.label ?? placeholder}
                  </span>
                  <ChevronsUpDown className="opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0"
                align="start"
                style={{
                  width: "var(--radix-popover-trigger-width)",
                  maxWidth: "var(--radix-popover-content-available-width)",
                }}
              >
                <Command>
                  <CommandInput
                    placeholder={searchPlaceholder}
                    className="h-9"
                  />
                  <CommandList>
                    <CommandEmpty>{emptyMessage}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => {
                        const isTruncated =
                          option.label.length > TRUNCATE_THRESHOLD;

                        const itemContent = (
                          <div className="flex min-w-0 items-center gap-2 w-full px-2 py-1.5">
                            <span className="truncate text-left">
                              {option.label}
                            </span>
                            <Check
                              className={cn(
                                "ml-auto shrink-0",
                                String(data[field]) === String(option.value)
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                          </div>
                        );

                        return (
                          <CommandItem
                            key={String(option.value)}
                            value={option.label}
                            className="p-0"
                            onSelect={() => handleSelect(String(option.value))}
                          >
                            {isTruncated ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {itemContent}
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  className="max-w-xs"
                                >
                                  {option.label}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              itemContent
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="default"
              onClick={saveChanges}
              disabled={processing}
              className="h-9 w-9 p-0"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancelEditing}
              disabled={processing}
              className="h-9 w-9 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {errors[field] && (
          <p className="text-xs text-red-500">{errors[field]}</p>
        )}
      </div>
    );
  }

  // Display mode - showing current value with edit button
  const currentOption = options.find(
    (option) => String(option.value) === String(value),
  );
  const displayValue = currentOption ? currentOption.label : formatValue(value);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </div>
      <div
        className={[
          "flex items-center justify-between gap-2 min-w-0 rounded-md px-2 py-1 -mx-2 focus:outline-none",
          "focus-visible:outline focus-visible:outline-primary/40",
          disabled
            ? "cursor-not-allowed bg-muted/40 text-muted-foreground"
            : "cursor-pointer hover:bg-muted/40 focus-within:bg-muted/40",
        ].join(" ")}
        onClick={() => {
          if (!disabled) startEditing();
        }}
        role={!disabled ? "button" : undefined}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            startEditing();
          }
        }}
      >
        <p className="font-medium truncate min-w-0" title={displayValue}>
          {displayValue}
        </p>
        {disabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Este campo no se puede editar</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  startEditing();
                }}
                aria-label={`Editar ${label}`}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              >
                <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Haz clic para editar</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
